"use client";

import * as DropdownMenuRadix from "@radix-ui/react-dropdown-menu";
import { ComponentProps, JSX, MouseEventHandler } from "react";
import { twJoin, twMerge } from "tailwind-merge";

export interface DropdownMenuOption {
  label: string;
  icon?: JSX.Element;
  onClick: (option: string) => void;
  disabled?: boolean;
  [key: string]: unknown;
}

interface DropdownMenuRootProps
  extends ComponentProps<typeof DropdownMenuRadix.Root> {}

interface DropdownMenuProps
  extends Omit<DropdownMenuRootProps, "onClick">,
    Omit<DropdownMenuRadix.DropdownMenuContentProps, "onClick"> {
  options: DropdownMenuOption[];
  className?: string;
  triggerClassName?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  arrow?: boolean;
}

export function DropdownMenu({
  options,
  children,
  open,
  align = "end",
  side = "bottom",
  alignOffset,
  sideOffset = 2,
  className,
  arrow,
  triggerClassName,
  onOpenChange,
}: DropdownMenuProps) {
  return (
    <DropdownMenuRadix.Root open={open} onOpenChange={onOpenChange}>
      <DropdownMenuRadix.Trigger className={triggerClassName}>
        {children}
      </DropdownMenuRadix.Trigger>
      <DropdownMenuRadix.Portal>
        <DropdownMenuRadix.Content
          side={side}
          align={align}
          alignOffset={alignOffset}
          sideOffset={sideOffset}
          className={twMerge(
            "bg-white shadow-[0px_2px_12px_0px_rgba(0,0,0,0.05)] py-1 border-[0.5px] border-neutral-300 rounded-[5px] z-10",
            className
          )}
        >
          {options.map((option, index) => (
            <DropdownMenuRadix.Item
              key={index}
              disabled={option.disabled}
              className={twJoin(
                "group py-2 flex items-center gap-x-4 px-3 cursor-pointer",
                "data-[disabled]:opacity-50 data-[disabled]:pointer-events-none",
                "hover:bg-neutral-100 hover:ring-0 hover:outline-none"
              )}
              onSelect={(event) => event.preventDefault()}
              onClick={(event) => {
                event?.stopPropagation();

                option.onClick(option.label);
              }}
            >
              {option.icon}
              <p className="text-xs font-medium text-neutral-500 group-hover:text-neutral-900">
                {option.label}
              </p>
            </DropdownMenuRadix.Item>
          ))}
          {arrow && <DropdownMenuRadix.Arrow className="fill-white" />}
        </DropdownMenuRadix.Content>
      </DropdownMenuRadix.Portal>
    </DropdownMenuRadix.Root>
  );
}
